<template>
  <div class="grade-page" v-loading="pageLoading">
    <!-- 顶栏 -->
    <header class="grade-header">
      <div class="grade-header__left">
        <el-button type="text" icon="el-icon-arrow-left" class="back-btn" @click="goBack">{{ gradeBackLabel }}</el-button>
        <span class="grade-header__divider">/</span>
        <span class="grade-header__title">寄售评级</span>
        <el-tag v-if="listed" type="success" size="mini" effect="plain">已上架</el-tag>
        <el-tag v-else-if="gradeCompleted" type="warning" size="mini" effect="plain">待用户上架</el-tag>
      </div>
      <div class="grade-header__score" v-if="displayGradeLevel">
        <span class="grade-badge">{{ displayGradeLevel }}</span>
        <span class="grade-score">{{ preview.compositeScore != null ? preview.compositeScore : "—" }}<small>/10</small></span>
      </div>
    </header>

    <!-- 订单摘要 -->
    <div class="order-bar" v-if="order">
      <span class="order-bar__item"><label>订单</label>{{ order.orderId || order.orderid }}</span>
      <span class="order-bar__item"><label>品牌</label>{{ order.brandName || "—" }}</span>
      <span class="order-bar__item"><label>型号</label>{{ order.typeName || "—" }}</span>
      <span class="order-bar__item" v-if="batchNum"><label>批号</label>{{ batchNum }}</span>
      <span class="order-bar__item"><label>寄售价</label>¥{{ order.jsPrice || "—" }}</span>
      <span class="order-bar__item" v-if="isAutoListOrder"><label>自动上架</label>是</span>
      <span class="order-bar__item order-bar__item--muted">标准 {{ standardVersion }}</span>
    </div>

    <el-alert
      v-if="order && order.jbjg === 0"
      type="error"
      :closable="false"
      title="鉴别为假，不可评级上架"
      show-icon
      class="alert-block"
    />

    <el-alert
      v-else-if="listed"
      type="success"
      :closable="false"
      title="该寄售单已上架；可修改下方商品描述并保存，将同步至对应闲置商品"
      show-icon
      class="alert-block"
    />

    <div class="grade-layout">
      <main class="grade-main">
        <!-- 参考图：瑕疵 / 细节 / 余量 -->
        <div class="ref-images-row">
          <div class="defect-panel ref-images-col">
            <div class="defect-panel__head">
              <span class="panel-title">瑕疵图</span>
              <span class="defect-panel__hint">点击查看大图，并在下方对照</span>
            </div>
            <div v-if="defectImages.length" class="img-grid img-grid--lightbox">
              <div
                v-for="(url, idx) in defectImages"
                :key="'defect-' + idx"
                class="img-thumb img-thumb--zoom"
                :class="{ 'img-thumb--active': defectPreviewIndex === idx }"
                @click="previewDefectImage(idx)"
              >
                <img :src="url" alt="" class="img-thumb__img" />
                <button
                  v-if="!listed"
                  type="button"
                  class="img-thumb__del"
                  title="删除"
                  @click.stop="removeDefectImage(idx)"
                >
                  <i class="el-icon-close"></i>
                </button>
              </div>
            </div>
            <el-empty v-else :image-size="56" description="暂无瑕疵图" />
          </div>

          <div class="defect-panel ref-images-col">
            <div class="defect-panel__head">
              <span class="panel-title">细节图</span>
              <span class="defect-panel__hint">点击查看大图</span>
            </div>
            <div v-if="detailImages.length" class="img-grid img-grid--lightbox">
              <div
                v-for="(url, idx) in detailImages"
                :key="'detail-' + idx"
                class="img-thumb img-thumb--zoom"
                @click="openPageImagePreview(detailImages, idx)"
              >
                <img :src="url" alt="" class="img-thumb__img" />
              </div>
            </div>
            <el-empty v-else :image-size="56" description="暂无细节图" />
          </div>

          <div class="defect-panel ref-images-col">
            <div class="defect-panel__head">
              <span class="panel-title">余量图</span>
              <span class="defect-panel__hint">点击查看大图</span>
            </div>
            <div v-if="remainImages.length" class="img-grid img-grid--lightbox">
              <div
                v-for="(url, idx) in remainImages"
                :key="'remain-' + idx"
                class="img-thumb img-thumb--zoom"
                @click="openPageImagePreview(remainImages, idx)"
              >
                <img :src="url" alt="" class="img-thumb__img" />
              </div>
            </div>
            <el-empty v-else :image-size="56" description="暂无余量图" />
          </div>
        </div>

        <div class="grade-section">
          <div class="panel-head">
            <div>
              <div class="panel-title">成色评级</div>
              <p class="panel-desc">按维度勾选缺陷，右侧实时显示综合分；每类扣分有上限</p>
            </div>
            <div class="panel-head__stat">
              已选 <strong>{{ selectedCount }}</strong> 项
            </div>
          </div>

          <el-tabs v-model="activeCategory" type="border-card" class="cat-tabs">
            <el-tab-pane
              v-for="cat in categories"
              :key="cat.key"
              :name="cat.key"
            >
              <span slot="label">
                {{ cat.name }}(上限{{ cat.maxDeduction }}分)
                <el-badge
                  v-if="categorySelectedCount(cat.key) > 0"
                  :value="categorySelectedCount(cat.key)"
                  class="cat-badge"
                />
              </span>
            </el-tab-pane>
          </el-tabs>

          <div class="grade-workbench grade-workbench--with-preview">
          <aside class="defect-preview-pane">
            <div class="defect-preview-pane__head">
              <span class="defect-preview-pane__title">瑕疵图预览</span>
              <span v-if="defectImages.length" class="defect-preview-pane__idx">{{ defectPreviewIndex + 1 }} / {{ defectImages.length }}</span>
            </div>
            <div class="defect-preview-pane__body">
              <template v-if="defectPreviewActive">
                <button
                  v-if="defectImages.length > 1"
                  type="button"
                  class="defect-preview-pane__nav defect-preview-pane__nav--prev"
                  @click="prevDefectPreview"
                >
                  <i class="el-icon-arrow-left"></i>
                </button>
                <img
                  :src="defectPreviewUrl"
                  alt=""
                  class="defect-preview-pane__img defect-preview-pane__img--zoom"
                  title="点击查看大图"
                  @click="openPageImagePreview(defectImages, defectPreviewIndex)"
                />
                <button
                  v-if="defectImages.length > 1"
                  type="button"
                  class="defect-preview-pane__nav defect-preview-pane__nav--next"
                  @click="nextDefectPreview"
                >
                  <i class="el-icon-arrow-right"></i>
                </button>
              </template>
              <el-empty v-else :image-size="64" description="暂无瑕疵图" />
            </div>
          </aside>

          <div class="grade-workbench__main">
            <div v-for="cat in categories" v-show="activeCategory === cat.key" :key="'panel-' + cat.key">
            <div class="cat-meta">
              {{ cat.name }}（上限 <b>{{ cat.maxDeduction }}</b> 分）
              <span v-if="categoryCappedDeduction(cat.key) > 0" class="cat-deduct">
                · 本类已扣 {{ categoryCappedDeduction(cat.key) }} 分
              </span>
            </div>
            <el-table
              :data="cat.items || []"
              size="small"
              border
              stripe
              class="defect-table"
              :style="{ width: defectTableWidth + 'px' }"
              :row-class-name="defectRowClass(cat.key)"
            >
              <el-table-column width="44" align="center">
                <template slot-scope="scope">
                  <el-checkbox
                    v-model="selectionMap[cat.key + '::' + scope.row.key].checked"
                    :disabled="listed"
                    @change="onSelectionChange"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="name" label="项目" width="200" show-overflow-tooltip />
              <el-table-column label="瑕疵类型" width="72" align="center">
                <template slot-scope="scope">
                  <span class="type-label">{{ defectTypeLabel(scope.row) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="扣分标准" width="72" align="center">
                <template slot-scope="scope">
                  <span class="deduct-val">{{ scope.row.deduction }}分</span>
                </template>
              </el-table-column>
              <el-table-column label="数量" width="96" align="center" class-name="qty-col">
                <template slot-scope="scope">
                  <el-input-number
                    v-if="scope.row.deductionType === 'QUANTIFIED'"
                    v-model="selectionMap[cat.key + '::' + scope.row.key].quantity"
                    :min="1"
                    :max="99"
                    size="mini"
                    controls-position="right"
                    class="qty-input"
                    :disabled="listed || !selectionMap[cat.key + '::' + scope.row.key].checked"
                    @change="onSelectionChange"
                  />
                  <span v-else class="text-muted">—</span>
                </template>
              </el-table-column>
            </el-table>
            <div class="manual-defect-block">
              <div class="manual-defect-block__title">罕见瑕疵（手动录入）</div>
              <div
                v-for="item in (customDefectsByCategory[cat.key] || [])"
                :key="'manual-' + cat.key + '-' + item.id"
                class="manual-defect-row"
              >
                <span class="manual-defect-row__name">{{ item.itemName }}</span>
                <span class="manual-defect-row__meta">扣 {{ item.unitDeduction }}分</span>
                <el-button
                  v-if="!listed"
                  type="text"
                  size="mini"
                  class="manual-defect-row__del"
                  @click="removeManualDefect(cat.key, item.id)"
                >删除</el-button>
              </div>
              <div v-if="!listed" class="manual-defect-form">
                <el-input
                  v-model="manualDraftByCategory[cat.key].itemName"
                  placeholder="瑕疵描述，如：瓶底轻微磨损"
                  size="small"
                  maxlength="50"
                  show-word-limit
                  class="manual-defect-form__name"
                  @keyup.enter.native="addManualDefect(cat)"
                />
                <el-input-number
                  v-model="manualDraftByCategory[cat.key].unitDeduction"
                  :min="0.5"
                  :max="cat.maxDeduction"
                  :step="0.5"
                  size="small"
                  controls-position="right"
                  class="manual-defect-form__deduct"
                />
                <span class="manual-defect-form__unit">分</span>
                <el-button
                  size="small"
                  type="primary"
                  plain
                  :disabled="!canAddManualDefect(cat)"
                  @click="addManualDefect(cat)"
                >添加</el-button>
              </div>
            </div>
            </div>
            <div v-if="categories.length > 1" class="cat-step-nav">
              <el-button size="small" @click="prevGradeCategory">上一项</el-button>
              <el-button size="small" type="primary" @click="nextGradeCategory">下一项</el-button>
            </div>
          </div>
          </div>

        <div class="extra-mobile">
          <div class="aside-extra__title">补充说明（成色 / 余量 / 剩余保质期）</div>
          <div class="attr-form">
            <div class="attr-row">
              <span class="attr-label">成色</span>
              <div class="attr-tags">
                <span
                  v-for="c in conditionList"
                  :key="'m-cond-' + c.value"
                  class="attr-tag"
                  :class="{ 'attr-tag--active': form.conditionLevel === c.value, 'is-disabled': listed }"
                  @click="toggleConditionLevel(c.value)"
                >{{ c.label }}</span>
              </div>
            </div>
            <div class="attr-row">
              <span class="attr-label">余量</span>
              <div class="attr-tags">
                <span
                  v-for="o in remainingListForUi"
                  :key="'m-rm-' + o.value"
                  class="attr-tag"
                  :class="{ 'attr-tag--active': form.remainingAmount === o.value, 'is-disabled': listed }"
                  @click="setAttrExtra('remainingAmount', o.value)"
                >{{ o.label }}</span>
              </div>
            </div>
            <div class="attr-row">
              <span class="attr-label">剩余保质期</span>
              <div class="attr-value">
                <p v-if="shelfLifeHint" class="attr-hint">{{ shelfLifeHint }}</p>
                <div class="attr-tags">
                  <span
                    v-for="o in shelfLifeList"
                    :key="'m-sl-' + o.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.shelfLife === o.value, 'is-disabled': listed }"
                    @click="setAttrExtra('shelfLife', o.value)"
                  >{{ o.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <div class="desc-section">
          <div class="panel-head">
            <div>
              <div class="panel-title">商品描述</div>
              <p class="panel-desc">保存评级时自动生成正文，可手动修改；固定话术展示时自动追加</p>
            </div>
            <div class="panel-head__stat desc-grade-tag">
              <span class="grade-badge">{{ displayGradeLevel || "—" }}</span>
              <span>{{ preview.compositeScore != null ? preview.compositeScore : "—" }}/10</span>
            </div>
          </div>

          <div class="listing-desc-preview listing-desc-preview--step">
            <el-input
              type="textarea"
              :rows="8"
              v-model="listingDescription"
              placeholder="保存评级后将自动生成描述正文，也可在此直接编辑（不含下方固定话术）"
            />
          </div>

          <div class="listing-desc-footer-block" v-if="listingDescriptionFooter">
            <div class="listing-desc-footer-block__label">固定话术（系统变量，展示时自动追加，不写入数据库）</div>
            <pre class="listing-desc-footer-block__text">{{ listingDescriptionFooter }}</pre>
          </div>

          <div class="listing-desc-footer-block listing-desc-footer-block--preview" v-if="listingDescriptionDisplay">
            <div class="listing-desc-footer-block__label">买家可见完整描述预览</div>
            <pre class="listing-desc-footer-block__text">{{ listingDescriptionDisplay }}</pre>
          </div>
        </div>

        <div class="step-actions">
          <el-button @click="goBack">返回列表</el-button>
          <el-button type="primary" plain :loading="saving" @click="saveGrade(false)">
            保存
          </el-button>
          <el-button v-if="!listed" :loading="nextLoading" @click="goNextOnly">
            直接下一单
          </el-button>
          <el-button v-if="!listed" type="primary" :loading="saving || nextLoading" @click="finishAndNext">
            完成并下一单
          </el-button>
        </div>
      </main>

      <!-- 右侧摘要（桌面端） -->
      <aside class="grade-aside" v-if="!listed">
        <div class="aside-card">
          <div class="aside-extra">
            <div class="aside-extra__title">补充说明（成色 / 余量 / 剩余保质期）</div>
            <div class="attr-form attr-form--aside">
              <div class="attr-row">
                <span class="attr-label">成色</span>
                <div class="attr-tags">
                  <span
                    v-for="c in conditionList"
                    :key="'cond-' + c.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.conditionLevel === c.value, 'is-disabled': listed }"
                    @click="toggleConditionLevel(c.value)"
                  >{{ c.label }}</span>
                </div>
              </div>
              <div class="attr-row">
                <span class="attr-label">余量</span>
                <div class="attr-tags">
                  <span
                    v-for="o in remainingListForUi"
                    :key="'rm-' + o.value"
                    class="attr-tag"
                    :class="{ 'attr-tag--active': form.remainingAmount === o.value, 'is-disabled': listed }"
                    @click="setAttrExtra('remainingAmount', o.value)"
                  >{{ o.label }}</span>
                </div>
              </div>
              <div class="attr-row">
                <span class="attr-label">剩余保质期</span>
                <div class="attr-value">
                  <p v-if="shelfLifeHint" class="attr-hint">{{ shelfLifeHint }}</p>
                  <div class="attr-tags">
                    <span
                      v-for="o in shelfLifeList"
                      :key="'sl-' + o.value"
                      class="attr-tag"
                      :class="{ 'attr-tag--active': form.shelfLife === o.value, 'is-disabled': listed }"
                      @click="setAttrExtra('shelfLife', o.value)"
                    >{{ o.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="aside-score">
            <div class="aside-score__level">{{ displayGradeLevel || "待评" }}</div>
            <div class="aside-score__num">
              {{ preview.compositeScore != null ? preview.compositeScore : "—" }}
              <small>/ 10</small>
            </div>
          </div>
          <ul
            class="aside-summary"
            :class="{ 'aside-summary--expanded': asideSummaryExpanded }"
            v-if="asideSummaryLines.length"
          >
            <li
              v-for="(line, i) in displayedAsideSummary"
              :key="'aside-sum-' + i"
              :class="{ 'aside-summary__attr': i < attrSummary.length }"
            >{{ line }}</li>
            <li
              v-if="!asideSummaryExpanded && asideSummaryRemainCount > 0"
              class="aside-summary__more aside-summary__toggle"
              @click="expandAsideSummary"
            >还有 {{ asideSummaryRemainCount }} 项，点击加载更多</li>
            <li
              v-else-if="asideSummaryCanCollapse"
              class="aside-summary__more aside-summary__toggle"
              @click="collapseAsideSummary"
            >收起</li>
          </ul>
          <p v-else class="aside-empty">暂未勾选缺陷与补充说明，默认 SS 级（10 分）</p>
          <div class="aside-actions">
            <el-button size="small" type="primary" :loading="saving" @click="saveGrade(false)">
              保存
            </el-button>
            <el-button size="small" :loading="nextLoading" @click="goNextOnly">
              直接下一单
            </el-button>
            <el-button size="small" type="primary" plain :loading="saving || nextLoading" @click="finishAndNext">
              完成并下一单
            </el-button>
          </div>
        </div>
      </aside>
    </div>

    <el-image-viewer
      v-if="pageImagePreviewVisible"
      ref="pageImageViewer"
      :url-list="pageImagePreviewList"
      :z-index="3000"
      :on-close="closePageImagePreview"
    />
  </div>
</template>

<script>
import ElImageViewer from "element-ui/packages/image/src/image-viewer";
const CONDITION_LIST = [
  { label: "全新带塑封", value: 1 },
  { label: "正装盒", value: 2 },
  { label: "简装盒", value: 3 },
  { label: "无盒", value: 4 }
];
const REMAINING_LIST = [
  { label: "满余量", value: "full" },
  { label: "仅试喷", value: "try" },
  { label: "0-1/3", value: "0_1_3" },
  { label: "1/3-2/3", value: "1_3_2_3" },
  { label: "2/3-1", value: "2_3_1" }
];
const SHELF_LIFE_LIST = [
  { label: "1年以上", value: "y1" },
  { label: "0-12个月", value: "m0_12" },
  { label: "已过期", value: "expired" },
  { label: "不详", value: "unknown" }
];
const REMAINING_LABEL_TO_VALUE = REMAINING_LIST.reduce((m, o) => { m[o.label] = o.value; return m; }, {});
const SHELF_LIFE_LABEL_TO_VALUE = SHELF_LIFE_LIST.reduce((m, o) => { m[o.label] = o.value; return m; }, {});
const CUSTOM_ITEM_KEY = "__custom__";
const ASIDE_SUMMARY_PREVIEW_COUNT = 6;

export default {
  name: "ConsignmentGrade",
  components: { ElImageViewer },
  data() {
    return {
      pageLoading: false,
      orderId: null,
      order: null,
      listed: false,
      gradeCompleted: false,
      activeCategory: "box",
      standardVersion: "v25.12.3",
      conditionList: CONDITION_LIST,
      remainingList: REMAINING_LIST,
      shelfLifeList: SHELF_LIFE_LIST,
      categories: [],
      selectionMap: {},
      customDefectsByCategory: {},
      manualDraftByCategory: {},
      customDefectSeq: 0,
      defectImages: [],
      detailImages: [],
      remainImages: [],
      defectPreviewIndex: null,
      pageImagePreviewVisible: false,
      pageImagePreviewList: [],
      pageImagePreviewIndex: 0,
      preview: { compositeScore: null, gradeLevel: "" },
      previewDetail: null,
      listingDescription: "",
      listingDescriptionFooter: "",
      /** 上次保存时的扣分快照，用于判断保存时是否重新生成描述 */
      lastSavedDeductionsKey: "",
      form: {
        conditionLevel: null,
        remainingAmount: "",
        shelfLife: ""
      },
      suggestedShelfLife: "",
      suggestedShelfLifeLabel: "",
      saving: false,
      nextLoading: false,
      previewTimer: null,
      asideSummaryExpanded: false
    };
  },
  computed: {
    gradeBackLabel() {
      const from = this.$route.query.from;
      if (from === "inspectionJob") return "鉴别作业";
      if (from === "officialProductList") return "官方寄售商品";
      return "寄售订单";
    },
    batchNum() {
      if (!this.order) return "";
      return this.order.batchNum || "";
    },
    shelfLifeHint() {
      if (!this.suggestedShelfLifeLabel) return "";
      const batch = this.batchNum;
      if (batch) {
        return "根据批号 " + batch + " 推荐：" + this.suggestedShelfLifeLabel + "（可修改）";
      }
      return "批号推荐：" + this.suggestedShelfLifeLabel + "（可修改）";
    },
    isAutoListOrder() {
      if (!this.order) return false;
      const raw = this.order.autoList != null ? this.order.autoList : this.order.auto_list;
      return Number(raw) === 1;
    },
    /** 满分 10 分为 SS；「全新」仅指包装成色，非评级等级 */
    displayGradeLevel() {
      return this.normalizeGradeLevel(this.preview.gradeLevel);
    },
    selectedCount() {
      return Object.keys(this.selectionMap).filter((k) => this.selectionMap[k] && this.selectionMap[k].checked).length;
    },
    selectedSummary() {
      const lines = [];
      if (!this.previewDetail || !this.previewDetail.deductions) return lines;
      (this.previewDetail.deductions || []).forEach((d) => {
        const qty = d.quantity > 1 ? " ×" + d.quantity : "";
        const manualTag = d.manual || d.itemKey === CUSTOM_ITEM_KEY ? " (手动)" : "";
        lines.push((d.categoryName || "") + " · " + (d.itemName || "") + qty + manualTag);
      });
      return lines;
    },
    attrSummary() {
      const lines = [];
      if (this.form.conditionLevel != null) {
        const label = this.labelForCondition(this.form.conditionLevel);
        if (label) lines.push("成色 · " + label);
      }
      if (this.form.remainingAmount) {
        const label = this.labelForRemaining(this.form.remainingAmount);
        if (label) lines.push("余量 · " + label);
      }
      if (this.form.shelfLife) {
        const label = this.labelForShelfLife(this.form.shelfLife);
        if (label) lines.push("剩余保质期 · " + label);
      }
      return lines;
    },
    asideSummaryLines() {
      return [...this.attrSummary, ...this.selectedSummary];
    },
    displayedAsideSummary() {
      if (this.asideSummaryExpanded) {
        return this.asideSummaryLines;
      }
      return this.asideSummaryLines.slice(0, ASIDE_SUMMARY_PREVIEW_COUNT);
    },
    asideSummaryRemainCount() {
      return Math.max(0, this.asideSummaryLines.length - ASIDE_SUMMARY_PREVIEW_COUNT);
    },
    asideSummaryCanCollapse() {
      return this.asideSummaryExpanded
        && this.asideSummaryLines.length > ASIDE_SUMMARY_PREVIEW_COUNT;
    },
    /** 全新带塑封时仅展示「满余量」 */
    remainingListForUi() {
      if (Number(this.form.conditionLevel) === 1) {
        return [{ label: "满余量", value: "full" }];
      }
      return this.remainingList;
    },
    listingDescriptionDisplay() {
      const body = (this.listingDescription || "").trim();
      const footer = (this.listingDescriptionFooter || "").trim();
      if (!body) return footer;
      if (!footer) return body;
      return body + "\n\n" + footer;
    },
    defectPreviewActive() {
      return this.defectPreviewIndex != null
        && this.defectPreviewIndex >= 0
        && this.defectImages.length > 0;
    },
    defectPreviewUrl() {
      if (!this.defectPreviewActive) return "";
      return this.defectImages[this.defectPreviewIndex] || "";
    },
    /** 列宽之和，避免表格右侧留白 */
    defectTableWidth() {
      return 44 + 200 + 72 + 72 + 96 + 2;
    }
  },
  watch: {
    "$route.query.orderId"(val) {
      const id = val ? Number(val) : null;
      if (!id || id === this.orderId) return;
      this.orderId = id;
      this.resetPageState();
      this.loadAll();
    }
  },
  created() {
    this.orderId = this.$route.query.orderId ? Number(this.$route.query.orderId) : null;
    if (!this.orderId) {
      this.$message.error("缺少订单ID");
      return;
    }
    this.loadAll();
  },
  methods: {
    goBack() {
      if (this.$route.query.from === "inspectionJob") {
        this.$router.push("/inspectionJob/index");
        return;
      }
      if (this.$route.query.from === "officialProductList") {
        this.$router.push("/secondhandOfficialProduct");
        return;
      }
      if (this.$route.query.from === "consignmentOrder") {
        this.$router.push("/goodsOrder/consignmentOrder");
        return;
      }
      this.$router.push("/goodsOrder/consignmentOrder");
    },
    resetPageState() {
      this.order = null;
      this.listed = false;
      this.gradeCompleted = false;
      this.defectImages = [];
      this.detailImages = [];
      this.remainImages = [];
      this.defectPreviewIndex = null;
      this.pageImagePreviewVisible = false;
      this.pageImagePreviewList = [];
      this.pageImagePreviewIndex = 0;
      this.preview = { compositeScore: null, gradeLevel: "" };
      this.previewDetail = null;
      this.listingDescription = "";
      this.lastSavedDeductionsKey = "";
      this.asideSummaryExpanded = false;
      this.form = { conditionLevel: null, remainingAmount: "", shelfLife: "" };
      this.suggestedShelfLife = "";
      this.suggestedShelfLifeLabel = "";
      this.initSelectionMap();
      this.initCustomDefects();
    },
    expandAsideSummary() {
      this.asideSummaryExpanded = true;
    },
    collapseAsideSummary() {
      this.asideSummaryExpanded = false;
    },
    selectDefectPreview(index) {
      if (index == null || index < 0 || index >= this.defectImages.length) return;
      this.defectPreviewIndex = index;
    },
    previewDefectImage(index) {
      this.selectDefectPreview(index);
      this.openPageImagePreview(this.defectImages, index);
    },
    prevDefectPreview() {
      if (!this.defectImages.length) return;
      const next = this.defectPreviewIndex <= 0
        ? this.defectImages.length - 1
        : this.defectPreviewIndex - 1;
      this.defectPreviewIndex = next;
    },
    nextDefectPreview() {
      if (!this.defectImages.length) return;
      const next = this.defectPreviewIndex >= this.defectImages.length - 1
        ? 0
        : this.defectPreviewIndex + 1;
      this.defectPreviewIndex = next;
    },
    prevGradeCategory() {
      const list = this.categories || [];
      if (list.length < 2) return;
      const idx = list.findIndex((c) => c.key === this.activeCategory);
      const nextIdx = idx <= 0 ? list.length - 1 : idx - 1;
      this.activeCategory = list[nextIdx].key;
    },
    nextGradeCategory() {
      const list = this.categories || [];
      if (list.length < 2) return;
      const idx = list.findIndex((c) => c.key === this.activeCategory);
      const nextIdx = idx >= list.length - 1 ? 0 : idx + 1;
      this.activeCategory = list[nextIdx].key;
    },
    syncDefectPreviewIndex() {
      if (!this.defectImages.length) {
        this.defectPreviewIndex = null;
        return;
      }
      if (this.defectPreviewIndex == null) {
        this.defectPreviewIndex = 0;
        return;
      }
      if (this.defectPreviewIndex >= this.defectImages.length) {
        this.defectPreviewIndex = this.defectImages.length - 1;
      }
    },
    openPageImagePreview(list, index) {
      if (!list || !list.length) return;
      const idx = index == null ? 0 : index;
      this.pageImagePreviewList = list.slice();
      this.pageImagePreviewIndex = idx;
      this.pageImagePreviewVisible = true;
      this.$nextTick(() => {
        const viewer = this.$refs.pageImageViewer;
        if (viewer && idx > 0) viewer.index = idx;
      });
    },
    closePageImagePreview() {
      this.pageImagePreviewVisible = false;
      this.pageImagePreviewList = [];
      this.pageImagePreviewIndex = 0;
    },
    removeDefectImage(index) {
      if (this.listed) return;
      this.$confirm("确定删除该瑕疵图？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        const next = this.defectImages.filter((_, i) => i !== index);
        return this.$https(this.$api.api.consignmentGradeDefectImages, {
          orderId: this.orderId,
          images: next
        }, "POST").then((res) => {
          const body = res.data || {};
          if (body.code === 200) {
            this.defectImages = next;
            this.syncDefectPreviewIndex();
            this.$message.success("已删除");
          } else {
            this.$message.error(body.msg || "删除失败");
          }
        });
      }).catch(() => {});
    },
    fetchNextPending() {
      return this.$https(this.$api.api.consignmentGradeNextPending, {
        currentOrderId: this.orderId
      }, "GET").then((res) => {
        const body = res.data || {};
        if (body.code !== 200) return null;
        return body.data || null;
      });
    },
    navigateToNext(next, successMsg) {
      if (next && next.orderId) {
        this.$message.success(successMsg);
        this.$router.replace({
          path: "/goodsOrder/consignmentGrade",
          query: {
            orderId: next.orderId,
            from: this.$route.query.from || undefined
          }
        });
        return;
      }
      this.$message.success("当前队列已全部完成");
      this.goBack();
    },
    goNextOnly() {
      if (this.listed) return;
      this.$confirm("当前单的修改不会保存，确定直接加载下一单？", "提示", {
        confirmButtonText: "直接下一单",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        this.nextLoading = true;
        return this.fetchNextPending();
      }).then((next) => {
        if (next === undefined) return;
        this.navigateToNext(next, "已加载下一单");
      }).catch(() => {}).finally(() => {
        this.nextLoading = false;
      });
    },
    finishAndNext() {
      if (this.listed) return;
      this.saveGrade(true).then((ok) => {
        if (!ok) return;
        this.nextLoading = true;
        return this.fetchNextPending();
      }).then((next) => {
        if (next === undefined) return;
        this.navigateToNext(next, "已保存，加载下一单");
      }).finally(() => {
        this.nextLoading = false;
      });
    },
    parseOrderImages(order) {
      if (!order || !order.images) return [];
      const raw = order.images;
      if (Array.isArray(raw)) return raw.filter(Boolean);
      if (typeof raw === "string") {
        const t = raw.trim();
        if (t.startsWith("[")) {
          try {
            return JSON.parse(t).filter(Boolean);
          } catch (e) {
            return [];
          }
        }
        return t.split(",").map((s) => s.trim()).filter(Boolean);
      }
      return [];
    },
    /** 瑕疵类型：原型为「每处」或「—」；兼容接口未返回 typeLabel 的情况 */
    defectTypeLabel(row) {
      if (!row) return "—";
      const label = row.typeLabel || row.type_label;
      if (label) return label;
      const dt = row.deductionType || row.deduction_type;
      if (dt === "QUANTIFIED") return "每处";
      return "—";
    },
    categorySelectedCount(catKey) {
      let n = 0;
      Object.keys(this.selectionMap).forEach((k) => {
        const s = this.selectionMap[k];
        if (s && s.checked && s.categoryKey === catKey) n++;
      });
      const manual = this.customDefectsByCategory[catKey] || [];
      return n + manual.length;
    },
    categoryCappedDeduction(catKey) {
      if (!this.previewDetail || !this.previewDetail.categorySummary) return 0;
      const row = (this.previewDetail.categorySummary || []).find((c) => c.categoryKey === catKey);
      return row && row.cappedDeduction != null ? row.cappedDeduction : 0;
    },
    defectRowClass(catKey) {
      return ({ row }) => {
        const s = this.selectionMap[catKey + "::" + row.key];
        return s && s.checked ? "defect-row--active" : "";
      };
    },
    loadAll() {
      this.pageLoading = true;
      this.fetchStandard()
        .then(() => this.fetchContext())
        .catch(() => this.$message.error("加载失败"))
        .finally(() => {
          this.pageLoading = false;
        });
    },
    fetchStandard() {
      return this.$https(this.$api.api.consignmentGradeStandard, {}, "GET").then((res) => {
        const body = res.data || {};
        if (body.code !== 200 || !body.data) return;
        this.standardVersion = body.data.version || this.standardVersion;
        this.categories = body.data.categories || [];
        if (this.categories.length && !this.categories.find((c) => c.key === this.activeCategory)) {
          this.activeCategory = this.categories[0].key;
        }
        this.initSelectionMap();
        this.initCustomDefects();
      });
    },
    fetchContext() {
      return this.$https(this.$api.api.consignmentGradeContext, { orderId: this.orderId }, "GET").then((res) => {
        const body = res.data || {};
        if (body.code !== 200) {
          this.$message.error(body.msg || "加载订单失败");
          return;
        }
        const data = body.data || {};
        this.order = data.order || null;
        this.listed = !!data.listed;
        this.gradeCompleted = !!data.gradeCompleted;
        this.defectImages = this.normalizeImageUrls(
          Array.isArray(data.defectImages) ? data.defectImages : []
        );
        if (!this.defectImages.length && data.inspectionJob && data.inspectionJob.defectImgs) {
          this.defectImages = this.normalizeImageUrls(this.parseImgsRaw(data.inspectionJob.defectImgs));
        }
        this.detailImages = this.normalizeImageUrls(
          Array.isArray(data.detailImages) ? data.detailImages : []
        );
        if (!this.detailImages.length) {
          this.detailImages = this.normalizeImageUrls(this.resolveDetailImagesFromContext(data));
        }
        this.remainImages = this.normalizeImageUrls(
          Array.isArray(data.remainImages) ? data.remainImages : []
        );
        if (!this.remainImages.length && data.inspectionJob && data.inspectionJob.remainImgs) {
          this.remainImages = this.normalizeImageUrls(this.parseImgsRaw(data.inspectionJob.remainImgs));
        }
        this.syncDefectPreviewIndex();
        const job = data.inspectionJob || {};
        this.suggestedShelfLife = data.suggestedShelfLife || "";
        this.suggestedShelfLifeLabel = data.suggestedShelfLifeLabel || "";
        this.restoreAttrExtra(data.gradeJson, job, this.suggestedShelfLife);
        if (data.compositeScore != null) this.preview.compositeScore = data.compositeScore;
        this.preview.gradeLevel = this.normalizeGradeLevel(data.gradeLevel
          || (data.gradeJson && data.gradeJson.gradeLevel)
          || "");
        if (data.gradeJson) {
          this.previewDetail = { ...data.gradeJson, gradeLevel: this.normalizeGradeLevel(data.gradeJson.gradeLevel) };
        }
        this.listingDescriptionFooter = data.listingDescriptionFooter || "";
        this.listingDescription = this.normalizeListingDescription(
          data.listingDescription
            || (data.gradeJson && data.gradeJson.listingDescription)
            || "",
          this.preview.gradeLevel,
          this.listingDescriptionFooter
        );
        this.restoreSelections(data.gradeJson);
        this.restoreManualDefects(data.gradeJson);
        this.syncLastSavedDeductionsKey();
        this.schedulePreview();
      });
    },
    normalizeGradeLevel(level) {
      if (!level) return "";
      const t = String(level).trim();
      return t === "全新" ? "SS" : t;
    },
    normalizeListingDescription(desc, gradeLevel, footer) {
      if (!desc) return "";
      let text = desc;
      const foot = (footer || this.listingDescriptionFooter || "").trim();
      if (foot) {
        const suffix = "\n\n" + foot;
        if (text.trim().endsWith(suffix)) {
          text = text.trim().slice(0, -suffix.length);
        } else if (text.trim().endsWith(foot)) {
          text = text.trim().slice(0, -foot.length).replace(/\n+$/, "");
        }
      }
      if (this.normalizeGradeLevel(gradeLevel) === "SS") {
        return text.replace(/【评级】全新/g, "【评级】SS");
      }
      return text;
    },
    isLikelyImageUrl(url) {
      if (!url || typeof url !== "string") return false;
      const t = url.trim();
      if (!t || t.startsWith("{") || t.startsWith("[") || t.includes('"img"')) return false;
      return /^https?:\/\//i.test(t) || t.startsWith("/");
    },
    normalizeImageUrls(urls) {
      const seen = new Set();
      return (urls || []).filter((u) => {
        if (!this.isLikelyImageUrl(u)) return false;
        const key = u.trim();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    parseImgsRaw(raw) {
      if (!raw) return [];
      if (Array.isArray(raw)) {
        return raw.filter((u) => this.isLikelyImageUrl(u));
      }
      if (typeof raw === "object") {
        const urls = [];
        ["img", "packImg", "image", "pack_image"].forEach((k) => {
          if (raw[k]) urls.push(raw[k]);
        });
        return urls.filter((u) => this.isLikelyImageUrl(u));
      }
      if (typeof raw === "string") {
        const t = raw.trim();
        if (!t) return [];
        if (t.startsWith("{")) {
          try {
            return this.parseImgsRaw(JSON.parse(t));
          } catch (e) {
            return [];
          }
        }
        if (t.startsWith("[")) {
          try {
            return this.parseImgsRaw(JSON.parse(t));
          } catch (e) {
            return [];
          }
        }
        return t.split(",").map((s) => s.trim()).filter((u) => this.isLikelyImageUrl(u));
      }
      return [];
    },
    resolveDetailImagesFromContext(data) {
      const urls = [];
      const order = (data && data.order) || {};
      const job = (data && data.inspectionJob) || {};
      this.pushRealpicture(urls, order.realpicture);
      this.pushRealpicture(urls, job.realpicture);
      this.pushImgsRaw(urls, order.images);
      this.pushImgsRaw(urls, job.productImage);
      return urls;
    },
    pushRealpicture(target, raw) {
      if (!raw) return;
      let obj = raw;
      if (typeof raw === "string") {
        const t = raw.trim();
        if (!t) return;
        if (t.startsWith("[")) {
          this.pushImgsRaw(target, t);
          return;
        }
        try {
          obj = JSON.parse(t);
        } catch (e) {
          return;
        }
      }
      if (Array.isArray(obj.urls)) {
        obj.urls.forEach((u) => {
          if (this.isLikelyImageUrl(u)) target.push(this.$assetUrl(u.trim()));
        });
      }
      ["img", "packImg", "img1", "img2", "img3", "img4"].forEach((k) => {
        if (this.isLikelyImageUrl(obj[k])) target.push(this.$assetUrl(obj[k].trim()));
      });
    },
    pushImgsRaw(target, raw) {
      this.parseImgsRaw(raw).forEach((u) => {
        const key = this.$assetUrl(u.trim());
        if (key && target.indexOf(key) === -1) target.push(key);
      });
    },
    initSelectionMap() {
      const map = {};
      this.categories.forEach((cat) => {
        (cat.items || []).forEach((item) => {
          const k = cat.key + "::" + item.key;
          map[k] = { checked: false, quantity: 1, categoryKey: cat.key, itemKey: item.key };
        });
      });
      this.selectionMap = map;
    },
    initCustomDefects() {
      const map = {};
      const drafts = {};
      (this.categories || []).forEach((cat) => {
        map[cat.key] = [];
        drafts[cat.key] = { itemName: "", unitDeduction: 0.5 };
      });
      this.customDefectsByCategory = map;
      this.manualDraftByCategory = drafts;
    },
    canAddManualDefect(cat) {
      if (!cat || this.listed) return false;
      const draft = this.manualDraftByCategory[cat.key];
      if (!draft) return false;
      const name = (draft.itemName || "").trim();
      if (!name) return false;
      const unit = Number(draft.unitDeduction);
      return !!(unit && unit >= 0.5 && unit <= cat.maxDeduction);
    },
    addManualDefect(cat) {
      if (!this.canAddManualDefect(cat)) return;
      const draft = this.manualDraftByCategory[cat.key];
      this.customDefectSeq += 1;
      this.customDefectsByCategory[cat.key].push({
        id: this.customDefectSeq,
        itemName: draft.itemName.trim(),
        unitDeduction: Number(draft.unitDeduction),
        quantity: 1
      });
      draft.itemName = "";
      draft.unitDeduction = 0.5;
      this.schedulePreview();
    },
    removeManualDefect(catKey, id) {
      if (this.listed) return;
      const list = this.customDefectsByCategory[catKey] || [];
      this.$set(
        this.customDefectsByCategory,
        catKey,
        list.filter((item) => item.id !== id)
      );
      this.schedulePreview();
    },
    restoreManualDefects(gradeJson) {
      this.initCustomDefects();
      if (!gradeJson || !gradeJson.deductions) return;
      (gradeJson.deductions || []).forEach((line) => {
        if (line.itemKey !== CUSTOM_ITEM_KEY) return;
        const catKey = line.categoryKey;
        if (!this.customDefectsByCategory[catKey]) {
          this.$set(this.customDefectsByCategory, catKey, []);
        }
        this.customDefectSeq += 1;
        this.customDefectsByCategory[catKey].push({
          id: this.customDefectSeq,
          itemName: line.itemName || "",
          unitDeduction: Number(line.unitDeduction) || 0.5,
          quantity: 1
        });
      });
    },
    restoreAttrExtra(gradeJson, job, suggestedShelfLife) {
      this.form.conditionLevel = null;
      this.form.remainingAmount = "";
      this.form.shelfLife = "";
      const extra = gradeJson && gradeJson.attrExtra;
      if (extra && typeof extra === "object") {
        if (extra.conditionLevel != null && extra.conditionLevel !== "") {
          this.form.conditionLevel = Number(extra.conditionLevel);
        }
        if (extra.remainingAmount) this.form.remainingAmount = extra.remainingAmount;
        if (extra.shelfLife) this.form.shelfLife = extra.shelfLife;
      }
      if (!this.form.remainingAmount && job && job.remainDesc && REMAINING_LABEL_TO_VALUE[job.remainDesc]) {
        this.form.remainingAmount = REMAINING_LABEL_TO_VALUE[job.remainDesc];
      }
      if (!this.form.shelfLife && job && job.shelfLifeDesc && SHELF_LIFE_LABEL_TO_VALUE[job.shelfLifeDesc]) {
        this.form.shelfLife = SHELF_LIFE_LABEL_TO_VALUE[job.shelfLifeDesc];
      }
      if (!this.form.shelfLife && suggestedShelfLife) {
        this.form.shelfLife = suggestedShelfLife;
      }
    },
    toggleConditionLevel(val) {
      if (this.listed) return;
      const prev = this.form.conditionLevel;
      if (this.form.conditionLevel === val) {
        this.form.conditionLevel = null;
        if (val === 1 && this.form.remainingAmount === "full") {
          this.form.remainingAmount = "";
        }
      } else {
        this.form.conditionLevel = val;
        if (val === 1) {
          this.form.remainingAmount = "full";
        } else if (prev === 1 && this.form.remainingAmount === "full") {
          this.form.remainingAmount = "";
        }
      }
    },
    setAttrExtra(key, val) {
      if (this.listed) return;
      if (key === "remainingAmount" && Number(this.form.conditionLevel) === 1) {
        if (val !== "full") return;
        this.form.remainingAmount = "full";
        return;
      }
      if (this.form[key] === val) {
        this.form[key] = "";
      } else {
        this.form[key] = val;
      }
    },
    labelForCondition(level) {
      const row = this.conditionList.find((c) => c.value === level);
      return row ? row.label : "";
    },
    labelForRemaining(code) {
      const row = this.remainingList.find((o) => o.value === code);
      return row ? row.label : "";
    },
    labelForShelfLife(code) {
      const row = this.shelfLifeList.find((o) => o.value === code);
      return row ? row.label : "";
    },
    buildSubmitExtra() {
      return {
        conditionLevel: this.form.conditionLevel,
        remainingAmount: this.form.remainingAmount || undefined,
        shelfLife: this.form.shelfLife || undefined,
        remainDesc: this.labelForRemaining(this.form.remainingAmount),
        shelfLifeDesc: this.labelForShelfLife(this.form.shelfLife)
      };
    },
    restoreSelections(gradeJson) {
      if (!gradeJson || !gradeJson.deductions) return;
      (gradeJson.deductions || []).forEach((line) => {
        if (line.itemKey === CUSTOM_ITEM_KEY) return;
        const k = line.categoryKey + "::" + line.itemKey;
        if (this.selectionMap[k]) {
          this.selectionMap[k].checked = true;
          this.selectionMap[k].quantity = line.quantity || 1;
        }
      });
    },
    collectDeductions() {
      const list = [];
      Object.keys(this.selectionMap).forEach((k) => {
        const s = this.selectionMap[k];
        if (s && s.checked) {
          list.push({ categoryKey: s.categoryKey, itemKey: s.itemKey, quantity: s.quantity || 1 });
        }
      });
      Object.keys(this.customDefectsByCategory || {}).forEach((catKey) => {
        (this.customDefectsByCategory[catKey] || []).forEach((item) => {
          list.push({
            categoryKey: catKey,
            itemKey: CUSTOM_ITEM_KEY,
            itemName: item.itemName,
            unitDeduction: item.unitDeduction,
            quantity: item.quantity || 1
          });
        });
      });
      return list;
    },
    deductionsKey(list) {
      const items = (list || []).map((d) => ({
        categoryKey: d.categoryKey || "",
        itemKey: d.itemKey || "",
        itemName: d.itemKey === CUSTOM_ITEM_KEY ? String(d.itemName || "").trim() : "",
        unitDeduction: d.itemKey === CUSTOM_ITEM_KEY ? Number(d.unitDeduction) : null,
        quantity: d.quantity == null ? 1 : Number(d.quantity)
      }));
      items.sort((a, b) => {
        const ka = [a.categoryKey, a.itemKey, a.itemName, a.unitDeduction, a.quantity].join("\0");
        const kb = [b.categoryKey, b.itemKey, b.itemName, b.unitDeduction, b.quantity].join("\0");
        return ka.localeCompare(kb);
      });
      return JSON.stringify(items);
    },
    deductionsChangedSinceLastSave() {
      return this.deductionsKey(this.collectDeductions()) !== this.lastSavedDeductionsKey;
    },
    syncLastSavedDeductionsKey() {
      this.lastSavedDeductionsKey = this.deductionsKey(this.collectDeductions());
    },
    onSelectionChange() {
      this.schedulePreview();
    },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.runPreview(), 280);
    },
    runPreview() {
      this.$https(this.$api.api.consignmentGradePreview, {
        orderId: this.orderId,
        deductions: this.collectDeductions()
      }, "POST").then((res) => {
        const body = res.data || {};
        if (body.code !== 200 || !body.data) return;
        this.preview.compositeScore = body.data.compositeScore;
        this.preview.gradeLevel = this.normalizeGradeLevel(body.data.gradeLevel || "");
        if (body.data.gradeJson) {
          const gj = typeof body.data.gradeJson === "string"
            ? JSON.parse(body.data.gradeJson) : body.data.gradeJson;
          gj.gradeLevel = this.normalizeGradeLevel(gj.gradeLevel);
          this.previewDetail = gj;
        }
      });
    },
    applyListingDescriptionFromJob(job) {
      if (!job || !job.gradeJson) return;
      try {
        const gj = typeof job.gradeJson === "string" ? JSON.parse(job.gradeJson) : job.gradeJson;
        gj.gradeLevel = this.normalizeGradeLevel(gj.gradeLevel || (job.gradeLevel || ""));
        this.previewDetail = gj;
        this.preview.gradeLevel = gj.gradeLevel;
        if (gj.compositeScore != null) {
          this.preview.compositeScore = gj.compositeScore;
        }
        this.listingDescription = this.normalizeListingDescription(
          gj.listingDescription || "",
          gj.gradeLevel,
          this.listingDescriptionFooter
        );
        this.syncLastSavedDeductionsKey();
      } catch (e) {
        /* ignore */
      }
    },
    saveGrade(goNext) {
      if (this.listed && goNext) return Promise.resolve(false);
      this.saving = true;
      const payload = {
        orderId: this.orderId,
        deductions: this.collectDeductions(),
        ...this.buildSubmitExtra()
      };
      const deductionsChanged = this.deductionsChangedSinceLastSave();
      if (!deductionsChanged && this.listingDescription && this.listingDescription.trim()) {
        payload.listingDescription = this.listingDescription.trim();
      }
      return this.$https(this.$api.api.consignmentGradeSubmit, payload, "POST").then((res) => {
        const body = res.data || {};
        if (body.code === 200) {
          this.applyListingDescriptionFromJob(body.data);
          if (!this.listed) {
            this.gradeCompleted = true;
            if (this.order) {
              this.$set(this.order, "status", 9);
            }
          }
          if (!goNext) {
            this.$message.success(this.listed ? "描述已保存并同步至商品" : "评级已保存");
          }
          return true;
        }
        this.$message.error(body.msg || "保存失败");
        return false;
      }).finally(() => {
        this.saving = false;
      });
    }
  }
};
</script>

<style lang="less" scoped>
.grade-page {
  min-height: calc(100vh - 80px);
  background: #f0f2f5;
  padding: 0 0 32px;
}

.grade-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 10;
}
.grade-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.back-btn {
  padding-left: 0;
  color: #606266;
}
.grade-header__divider {
  color: #dcdfe6;
}
.grade-header__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.grade-header__score {
  display: flex;
  align-items: center;
  gap: 10px;
}
.grade-badge {
  display: inline-block;
  min-width: 36px;
  padding: 4px 10px;
  background: #111;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  border-radius: 4px;
  text-align: center;
}
.grade-score {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  small { font-size: 13px; color: #909399; font-weight: 400; }
}

.order-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  padding: 10px 14px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  color: #303133;
}
.order-bar__item label {
  color: #909399;
  margin-right: 6px;
}
.order-bar__item--muted {
  color: #909399;
  margin-left: auto;
}

.alert-block {
  margin: 12px 24px 0;
}

.grade-layout {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0 0 10px;
  max-width: none;
  width: 100%;
  box-sizing: border-box;
}

.grade-main {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}
.panel-head__stat {
  font-size: 13px;
  color: #606266;
  strong { color: #e6a23c; font-size: 16px; }
}
.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}
.panel-desc {
  font-size: 12px;
  color: #909399;
  margin: 0 0 16px;
}

.ref-images-row {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.ref-images-col {
  flex: 1;
  min-width: 200px;
  margin-bottom: 0;
}
.desc-section {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
  width: 100%;
  text-align: left;
  .panel-head {
    margin-bottom: 12px;
  }
  .listing-desc-preview--step {
    max-width: 100%;
  }
}

.grade-section {
  margin-bottom: 12px;
  .panel-head {
    margin-bottom: 12px;
  }
}

.grade-workbench {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.grade-workbench__main {
  flex: 0 0 auto;
  width: fit-content;
  max-width: 100%;
  min-width: 0;
}
.grade-workbench--with-preview {
  width: 100%;
  .defect-preview-pane {
    flex: 0 0 360px;
    width: 360px;
    min-width: 360px;
    max-width: 360px;
  }
}
.defect-preview-pane {
  width: 360px;
  flex-shrink: 0;
  align-self: flex-start;
  position: sticky;
  top: 16px;
  z-index: 9;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
}
.defect-preview-pane__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.defect-preview-pane__title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  flex: 1;
}
.defect-preview-pane__idx {
  font-size: 12px;
  color: #909399;
}
.defect-preview-pane__body {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 240px;
  max-height: 480px;
  overflow: hidden;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #eee;
  padding: 0;
  /deep/ .el-empty {
    padding: 24px 0;
  }
}
.defect-preview-pane__img {
  width: 100%;
  max-width: 100%;
  max-height: 440px;
  object-fit: contain;
  display: block;
  user-select: none;
  &--zoom {
    cursor: zoom-in;
    &:hover { opacity: 0.96; }
  }
}
.defect-preview-pane__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: background 0.15s;
  &:hover {
    background: rgba(0, 0, 0, 0.65);
  }
  &--prev { left: 8px; }
  &--next { right: 8px; }
}

.defect-panel {
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #eee;
}
.defect-panel__head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
  .panel-title { margin-bottom: 0; }
}
.img-thumb--zoom {
  cursor: zoom-in;
  &:hover { opacity: 0.92; }
}
.defect-panel__hint {
  font-size: 12px;
  color: #909399;
}
.img-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.img-thumb {
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  &--active {
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.25);
  }
}
.img-thumb__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.img-thumb__tag {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  text-align: center;
  line-height: 18px;
}
.img-thumb__del {
  position: absolute;
  top: 4px; right: 4px;
  width: 20px; height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  cursor: pointer;
  padding: 0;
  line-height: 20px;
  font-size: 12px;
}
.img-add {
  width: 88px;
  height: 88px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 11px;
  cursor: pointer;
  transition: border-color 0.2s;
  i { font-size: 20px; margin-bottom: 4px; }
  &:hover { border-color: #409eff; color: #409eff; }
  &.is-loading { pointer-events: none; opacity: 0.7; }
}

.cat-tabs {
  margin-bottom: 12px;
  width: 100%;
  /deep/ .el-tabs__header {
    margin: 0;
  }
  /deep/ .el-tabs__content {
    display: none;
  }
  /deep/ .el-tabs__item {
    padding: 0 14px;
    height: 38px;
    line-height: 38px;
    font-size: 13px;
  }
  /deep/ .el-tabs__nav-wrap {
    overflow: hidden;
  }
  /deep/ .el-tabs__nav-scroll {
    overflow-x: auto;
    overflow-y: hidden;
  }
}
.cat-badge /deep/ .el-badge__content {
  height: 16px;
  line-height: 16px;
  padding: 0 5px;
}
.cat-meta {
  font-size: 12px;
  color: #909399;
  margin-bottom: 10px;
  b { color: #606266; }
}
.cat-deduct { color: #e6a23c; }

.defect-table {
  /deep/ .defect-row--active { background: #fdf6ec !important; }
  /deep/ colgroup col[name="gutter"] {
    width: 0 !important;
    display: none;
  }
  /deep/ th.gutter,
  /deep/ td.gutter {
    width: 0 !important;
    display: none;
  }
  /deep/ .el-table__body-wrapper,
  /deep/ .el-table__header-wrapper {
    overflow: hidden;
  }
  /deep/ .qty-col .cell {
    padding-left: 4px;
    padding-right: 4px;
  }
  /deep/ .qty-input.el-input-number--mini {
    width: 84px;
  }
  /deep/ .qty-input.el-input-number--mini .el-input__inner {
    padding-left: 6px;
    padding-right: 32px;
  }
}
.deduct-val {
  color: #e6a23c;
  font-weight: 600;
  font-size: 13px;
}
.type-label {
  color: #606266;
  font-size: 12px;
}
.text-muted { color: #c0c4cc; }

.manual-defect-block {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  max-width: 486px;
}
.manual-defect-block__title {
  font-size: 12px;
  color: #606266;
  font-weight: 600;
  margin-bottom: 8px;
}
.manual-defect-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-of-type { border-bottom: none; }
}
.manual-defect-row__name {
  flex: 1;
  color: #303133;
  word-break: break-all;
}
.manual-defect-row__meta {
  color: #e6a23c;
  white-space: nowrap;
}
.manual-defect-row__del { color: #f56c6c; padding: 0; }
.manual-defect-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.manual-defect-form__name {
  flex: 1 1 200px;
  min-width: 160px;
}
.manual-defect-form__deduct {
  width: 88px;
}
.manual-defect-form__unit {
  font-size: 12px;
  color: #909399;
}

.attr-form {
  padding-top: 4px;
}
.attr-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}
.attr-value {
  flex: 1;
  min-width: 0;
}
.attr-hint {
  margin: 0 0 6px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.attr-label {
  flex-shrink: 0;
  width: 72px;
  font-size: 13px;
  color: #606266;
  line-height: 28px;
  text-align: right;
}
.attr-tags {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.attr-tag {
  display: inline-block;
  padding: 4px 12px;
  font-size: 12px;
  color: #606266;
  background: #f4f4f5;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
  &:hover:not(.is-disabled) {
    border-color: #409eff;
    color: #409eff;
  }
  &--active {
    color: #409eff;
    background: #ecf5ff;
    border-color: #b3d8ff;
  }
  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
}

.listing-desc-preview {
  margin-top: 8px;
  /deep/ .el-textarea__inner {
    font-family: inherit;
    line-height: 1.65;
    font-size: 13px;
    color: #303133;
    background: #fafafa;
  }
  &--step /deep/ .el-textarea__inner {
    min-height: 280px;
    padding: 16px;
  }
}

.listing-desc-footer-block {
  margin-top: 16px;
  padding: 12px 14px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 8px;

  &__label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 8px;
  }

  &__text {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
    line-height: 1.65;
    color: #606266;
    font-family: inherit;
  }

  &--preview {
    background: #fafafa;
    border-style: dashed;
  }
}

.desc-grade-tag {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #606266;
}

.publish-preview {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 20px;
}
.publish-preview__score {
  flex-shrink: 0;
  text-align: center;
  min-width: 72px;
}
.publish-preview__level {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #111;
}
.publish-preview__num {
  font-size: 12px;
  color: #909399;
}
.publish-preview__imgs {
  display: flex;
  gap: 6px;
  align-items: center;
  img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #eee;
  }
}
.publish-preview__more {
  font-size: 12px;
  color: #909399;
}
.publish-form { max-width: 640px; }
.form-unit { margin-left: 8px; color: #909399; font-size: 13px; }

.step-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.grade-aside {
  width: 240px;
  flex-shrink: 0;
  position: sticky;
  top: 16px;
}
.aside-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.aside-extra {
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}
.aside-extra__title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  line-height: 1.4;
}
.attr-form--aside {
  padding-top: 0;
  .attr-row {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    margin-bottom: 12px;
    &:last-child { margin-bottom: 0; }
  }
  .attr-label {
    width: auto;
    text-align: left;
    line-height: 1.4;
    font-size: 12px;
    color: #909399;
  }
  .attr-tag {
    padding: 3px 8px;
    font-size: 11px;
  }
}
.aside-score {
  text-align: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 12px;
}
.aside-score__level {
  font-size: 28px;
  font-weight: 700;
  color: #111;
  line-height: 1.2;
}
.aside-score__num {
  font-size: 14px;
  color: #606266;
  margin-top: 4px;
  small { color: #909399; }
}
.aside-summary {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  font-size: 11px;
  color: #606266;
  line-height: 1.6;
  max-height: 120px;
  overflow-y: auto;
  li { padding: 2px 0; }
}
.aside-summary--expanded {
  max-height: 280px;
}
.aside-summary__attr {
  color: #409eff;
}
.aside-summary__more { color: #909399; }
.aside-summary__toggle {
  cursor: pointer;
  color: #409eff;
  user-select: none;
  &:hover { text-decoration: underline; }
}
.aside-empty {
  font-size: 12px;
  color: #c0c4cc;
  text-align: center;
  margin: 0 0 12px;
}
.aside-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /deep/ .el-button { margin: 0; width: 100%; }
}

@media (max-width: 960px) {
  .grade-aside { display: none; }
  .grade-workbench {
    flex-direction: column;
  }
  .grade-workbench--with-preview .grade-workbench__main {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
  }
  .grade-workbench--with-preview .defect-preview-pane {
    min-width: 0;
    width: 100%;
  }
  .defect-preview-pane {
    width: 100%;
    position: static;
  }
  .extra-mobile {
    display: block;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
  .grade-layout { padding: 6px 8px 0; }
}
.extra-mobile { display: none; }
</style>



// WEBPACK FOOTER //
// src/pages/consignmentGrade/index.vue